import React from 'react';
import { useIntl } from 'react-intl';
import { WithStylesProps } from 'react-jss';

import { black5 } from '~/assets/shared-styles/general';
import { EM_DASH } from '~/common/constants/unicode';
import EcMultipleSelectValue from '~/components/Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import FieldValuesMultiSelect from '~/components/Shared/FieldValuesMultiSelect/FieldValuesMultiSelect';
import SubArrowIcon from '~/components/Shared/Icons/SubArrowIcon';

import styles from '../InformationField.styles';
import { Field } from '../InformationFieldV2';

interface MultipleArrayFieldProps extends WithStylesProps<typeof styles> {
  field: Field;
  editMode: boolean;
  fieldValue: any;
  setFieldValue: (value: any) => void;
  disableSelectAllFlag: boolean;
}

export const MultipleArrayField = ({
  classes,
  field,
  editMode,
  fieldValue,
  setFieldValue,
  disableSelectAllFlag,
}: MultipleArrayFieldProps) => {
  const intl = useIntl();

  const renderMultipleValues = ({ is_editable }: { is_editable?: boolean }) => {
    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {fieldValue?.length ? (
            fieldValue.map((value: any, index: number) => (
              <EcMultipleSelectValue
                label={value.display_value}
                key={index}
                closeHandler={() => {
                  const newState = fieldValue.filter(
                    (item: any) => item.value !== value.value,
                  );
                  setFieldValue(newState);
                }}
                isDisabled={!is_editable}
              />
            ))
          ) : (
            <span className={classes.multipleValues}>
              {intl.formatMessage({
                id: 'InformationField.noValuesSelectedMessage',
                defaultMessage: 'No values selected...',
                description:
                  'Message when no values are selected for multiple select field',
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (editMode) {
    const presentValue =
      fieldValue && fieldValue.length
        ? fieldValue.map((val: any) => ({
            label: val.display_value,
            value: val.value,
          }))
        : [];

    return (
      <div className={classes.editItemContainer}>
        <FieldValuesMultiSelect
          aria-label={field.name}
          value={presentValue}
          onChange={(newItems: any) => {
            const newState = newItems.map((item: any) => ({
              display_value: item.label,
              value: item.value,
            }));
            setFieldValue(newState);
          }}
          width="100%"
          placeholder={intl.formatMessage(
            {
              id: 'InformationField.searchFieldPlaceholder',
              defaultMessage: 'Search for {fieldName}...',
              description:
                'Placeholder text for multi-select search field. Example: fieldName = "Contract Type"',
            },
            { fieldName: field.name },
          )}
          noOptionsMessage={intl.formatMessage(
            {
              id: 'InformationField.noOptionsMessage',
              defaultMessage: 'No {fieldName} found',
              description:
                'Message when no options are found in search. Example: fieldName = "Contract Type"',
            },
            { fieldName: field.name },
          )}
          isDisabled={!field.is_editable}
          isCreatable={field.allow_new_options}
          useMinInputLength={['Counterparties', 'Internal Parties'].includes(
            field.class_name || '',
          )}
          allowSelectAll
          disableSelectAll={disableSelectAllFlag}
          fieldId={field.id}
          fieldValues={field.values}
        />
        {renderMultipleValues(field)}
      </div>
    );
  }

  return fieldValue && fieldValue.length
    ? fieldValue.map((val: any) => val.display_value).join(' • ')
    : EM_DASH;
};
